import { useState, useCallback } from 'react';
import { Change } from '../components/ChangesTable';
import { apiService } from '../services/api';

async function enrichChangeWithLegislationId(change: Change): Promise<Change> {
  if (change.legislation_id) return change;
  try {
    const full = await apiService.getChangeById(change.id);
    return { ...change, legislation_id: (full as { legislation_id?: number }).legislation_id };
  } catch {
    return change;
  }
}

export interface UseChangesTableProps {
  initialPage?: number;
  initialRowsPerPage?: number;
}

export interface UseChangesTableReturn {
  // Estados
  selectedChange: Change | null;
  detailsOpen: boolean;
  emailDialogOpen: boolean;
  page: number;
  rowsPerPage: number;

  // Handlers
  handleRowClick: (change: Change) => void;
  handleViewDetails: (change: Change) => void;
  handleCloseDetails: () => void;
  handleSendEmail: (emailData: { to: string; subject: string; item: any }) => Promise<void>;
  handleCloseEmailDialog: () => void;
  handlePageChange: (newPage: number) => void;
  handleRowsPerPageChange: (newRowsPerPage: number) => void;
  clearSelection: () => void;
}

/**
 * Hook customizado para gerenciar o estado e lógica da ChangesTable
 * 
 * @param initialPage - Página inicial (padrão: 1)
 * @param initialRowsPerPage - Linhas por página inicial (padrão: 10)
 * @returns Objeto com estados e handlers para a tabela
 * 
 * @example
 * ```tsx
 * const tableState = useChangesTable({ initialPage: 1, initialRowsPerPage: 25 });
 * 
 * return (
 *   <ChangesTable
 *     data={data}
 *     onRowClick={tableState.handleRowClick}
 *     onViewDetails={tableState.handleViewDetails}
 *     onSendEmail={tableState.handleSendEmail}
 *     pagination={{
 *       enabled: true,
 *       page: tableState.page,
 *       rowsPerPage: tableState.rowsPerPage,
 *       totalRows: totalCount,
 *       onPageChange: tableState.handlePageChange,
 *       onRowsPerPageChange: tableState.handleRowsPerPageChange,
 *     }}
 *   />
 * );
 * ```
 */
export function useChangesTable({
  initialPage = 1,
  initialRowsPerPage = 10,
}: UseChangesTableProps = {}): UseChangesTableReturn {
  // Estados
  const [selectedChange, setSelectedChange] = useState<Change | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);

  // Handler para clique na linha
  const handleRowClick = useCallback((change: Change) => {
    enrichChangeWithLegislationId(change).then(setSelectedChange);
    setDetailsOpen(true);
  }, []);

  // Handler para ver detalhes
  const handleViewDetails = useCallback((change: Change) => {
    enrichChangeWithLegislationId(change).then(setSelectedChange);
    setDetailsOpen(true);
  }, []);

  // Handler para fechar detalhes
  const handleCloseDetails = useCallback(() => {
    setDetailsOpen(false);
    // Mantém selectedChange para permitir animações de saída
    setTimeout(() => setSelectedChange(null), 300);
  }, []);

  // Handler para enviar email
  const handleSendEmail = useCallback((emailData: { to: string; subject: string; item: any }) => {
    setSelectedChange(emailData.item);
    setEmailDialogOpen(true);
    return Promise.resolve();
  }, []);

  // Handler para fechar dialog de email
  const handleCloseEmailDialog = useCallback(() => {
    setEmailDialogOpen(false);
    // Mantém selectedChange quando o e-mail foi aberto a partir da modal de detalhes
  }, []);

  // Handler para mudança de página
  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  // Handler para mudança de linhas por página
  const handleRowsPerPageChange = useCallback((newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1); // Reset para primeira página ao mudar o tamanho
  }, []);

  // Limpar seleção
  const clearSelection = useCallback(() => {
    setSelectedChange(null);
    setDetailsOpen(false);
    setEmailDialogOpen(false);
  }, []);

  return {
    // Estados
    selectedChange,
    detailsOpen,
    emailDialogOpen,
    page,
    rowsPerPage,

    // Handlers
    handleRowClick,
    handleViewDetails,
    handleCloseDetails,
    handleSendEmail,
    handleCloseEmailDialog,
    handlePageChange,
    handleRowsPerPageChange,
    clearSelection,
  };
}




